import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import AdminAuditLog from '../components/AdminAuditLog';
import ProfileAvatar from '../components/ProfileAvatar';

interface User {
  id: string;
  user_id: string;
  username: string;
  name: string;
  email: string;
  description: string;
  role: string;
  profileComplete: boolean;
  account_status: string;
  profile_image_url?: string;
  created_at: string;
  updated_at: string;
  locked_at?: string;
  locked_by?: string;
  lock_reason?: string;
  unlocked_at?: string;
  unlocked_by?: string;
}

interface LockedUser {
  user_id: string;
  username: string;
  email: string;
  name: string;
  profile_image_url?: string;
  locked_at: string;
  locked_by: string;
  lock_reason: string;
  created_at: string;
}

interface LockedUsersResponse {
  locked_users: LockedUser[];
  total_count: number;
  support_email: string;
}

const AdminUsersPage: React.FC = () => {
  const { userRole } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [lockedUsers, setLockedUsers] = useState<LockedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocked, setIsLoadingLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [supportEmail, setSupportEmail] = useState('support@ratemy.talk');
  const [showLockModal, setShowLockModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | LockedUser | null>(null);
  const [lockReason, setLockReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'locked'>('search');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [selectedUserForPromotion, setSelectedUserForPromotion] = useState<User | null>(null);
  const [promotionMessage, setPromotionMessage] = useState('');
  const [isPromoting, setIsPromoting] = useState(false);
  const [promotionSuccess, setPromotionSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (userRole === 'admin') {
      loadLockedUsers();
    }
  }, [userRole]);

  const loadLockedUsers = async () => {
    try {
      setIsLoadingLocked(true);
      setError(null);

      const response = await apiClient.get('/v1/admin/users/locked');
      const data: LockedUsersResponse = response.data;

      setLockedUsers(data.locked_users || []);
      setSupportEmail(data.support_email || 'support@ratemy.talk');
    } catch (err: any) {
      console.error('Failed to load locked users:', err);
      setError(err.response?.data?.error?.message || 'Failed to load locked users');
      setLockedUsers([]); // Ensure lockedUsers is always an array
    } finally {
      setIsLoadingLocked(false);
    }
  };

  const performSearch = async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/users/search', {
        params: {
          q: query.trim(),
          page,
          limit: pagination.limit
        }
      });

      // Access the nested data from the API response
      const apiResponse = response.data;
      const data = apiResponse.data;

      setSearchResults(data.users || []);
      setPagination(apiResponse.metadata?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      setHasSearched(true);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery, 1);
  };

  const handlePageChange = (newPage: number) => {
    performSearch(searchQuery, newPage);
  };

  const handleLockUser = (user: User) => {
    setSelectedUser(user);
    setLockReason('');
    setShowLockModal(true);
  };

  const handleUnlockUser = (user: User | LockedUser) => {
    setSelectedUser(user);
    setShowUnlockModal(true);
  };

  const confirmLockUser = async () => {
    if (!selectedUser) return;

    try {
      setIsProcessing(true);
      setError(null);

      await apiClient.put(`/v1/admin/users/${selectedUser.user_id}/lock`, {
        reason: lockReason.trim() || undefined
      });

      // Refresh data
      if (activeTab === 'search' && hasSearched) {
        await performSearch(searchQuery, pagination.page);
      }
      await loadLockedUsers();

      setShowLockModal(false);
      setSelectedUser(null);
      setLockReason('');
    } catch (err: any) {
      console.error('Failed to lock user:', err);
      setError(err.response?.data?.error?.message || 'Failed to lock user account');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmUnlockUser = async () => {
    if (!selectedUser) return;

    try {
      setIsProcessing(true);
      setError(null);

      await apiClient.put(`/v1/admin/users/${selectedUser.user_id}/unlock`);

      // Refresh data
      if (activeTab === 'search' && hasSearched) {
        await performSearch(searchQuery, pagination.page);
      }
      await loadLockedUsers();

      setShowUnlockModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Failed to unlock user:', err);
      setError(err.response?.data?.error?.message || 'Failed to unlock user account');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePromoteUser = (user: User) => {
    setSelectedUserForPromotion(user);
    setPromotionMessage('');
    setShowPromoteModal(true);
  };

  const confirmPromoteUser = async () => {
    if (!selectedUserForPromotion) return;

    try {
      setIsPromoting(true);
      setError(null);

      await apiClient.post('/v1/admin/promote', {
        email: selectedUserForPromotion.email,
        message: promotionMessage.trim() || undefined
      });

      // Show success message
      setPromotionSuccess(`${selectedUserForPromotion.name || selectedUserForPromotion.email} has been promoted to admin successfully!`);

      // Refresh data
      if (activeTab === 'search' && hasSearched) {
        await performSearch(searchQuery, pagination.page);
      }

      setShowPromoteModal(false);
      setSelectedUserForPromotion(null);
      setPromotionMessage('');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setPromotionSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Failed to promote user:', err);
      setError(err.response?.data?.error?.message || 'Failed to promote user to admin');
      setShowPromoteModal(false);
    } finally {
      setIsPromoting(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'locked': return 'bg-danger';
      case 'active': return 'bg-success';
      default: return 'bg-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'locked': return 'fas fa-lock';
      case 'active': return 'fas fa-check-circle';
      default: return 'fas fa-question-circle';
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>
                <i className="fas fa-users-cog me-2 text-primary"></i>
                User Management
              </h1>
              <p className="text-muted mb-0">
                Manage user accounts, lock/unlock access, and view account status
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {promotionSuccess && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Success!</strong> {promotionSuccess}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setPromotionSuccess(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'search' ? 'active' : ''}`}
                onClick={() => setActiveTab('search')}
              >
                <i className="fas fa-search me-2"></i>
                Search Users
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'locked' ? 'active' : ''}`}
                onClick={() => setActiveTab('locked')}
              >
                <i className="fas fa-lock me-2"></i>
                Locked Users
                {lockedUsers && lockedUsers.length > 0 && (
                  <span className="badge bg-danger ms-2">{lockedUsers.length}</span>
                )}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Search Users Tab */}
      {activeTab === 'search' && (
        <>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="row">
                      <div className="col-md-10">
                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fas fa-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search users by name or email address..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setSearchQuery('');
                                setSearchResults([]);
                                setHasSearched(false);
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-md-2">
                        <button
                          type="submit"
                          className="btn btn-primary w-100"
                          disabled={isLoading || !searchQuery.trim()}
                        >
                          {isLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            'Search'
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {hasSearched && (
            <div className="row mt-4">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Search Results
                      {pagination.total > 0 && (
                        <span className="badge bg-primary ms-2">{pagination.total}</span>
                      )}
                    </h5>
                    {searchQuery && (
                      <small className="text-muted">
                        Searching for: "{searchQuery}"
                      </small>
                    )}
                  </div>
                  <div className="card-body">
                    {searchResults.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-search fa-4x text-muted mb-4"></i>
                        <h4 className="text-muted">No users found</h4>
                        <p className="text-muted mb-4">
                          We couldn't find any users matching "{searchQuery}".
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th style={{ width: '60px' }}></th>
                                <th>User</th>
                                <th>Role</th>
                                <th>Account Status</th>
                                <th>Profile</th>
                                <th>Joined</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {searchResults.map((user) => (
                                <tr key={user.id}>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <ProfileAvatar
                                      imageUrl={user.profile_image_url}
                                      name={user.name || 'Unknown User'}
                                      size="sm"
                                    />
                                  </td>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <div>
                                      <strong>{user.name || 'No name provided'}</strong>
                                      <br />
                                      <small className="text-muted">{user.email}</small>
                                      {user.username && user.username !== user.email && (
                                        <>
                                          <br />
                                          <small className="text-muted">@{user.username}</small>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                                      {user.role === 'admin' ? (
                                        <>
                                          <i className="fas fa-crown me-1"></i>
                                          ADMIN
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-user me-1"></i>
                                          USER
                                        </>
                                      )}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge ${getStatusBadgeClass(user.account_status || 'active')}`}>
                                      <i className={`${getStatusIcon(user.account_status || 'active')} me-1`}></i>
                                      {(user.account_status || 'active').toUpperCase()}
                                    </span>
                                    {user.account_status === 'locked' && user.locked_at && (
                                      <div className="mt-1">
                                        <small className="text-muted">
                                          Locked {new Date(user.locked_at).toLocaleDateString()}
                                        </small>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {user.profileComplete ? (
                                      <span className="badge bg-success">
                                        <i className="fas fa-check-circle me-1"></i>
                                        Complete
                                      </span>
                                    ) : (
                                      <span className="badge bg-warning text-dark">
                                        <i className="fas fa-exclamation-circle me-1"></i>
                                        Incomplete
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                                    </small>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      <Link
                                        to={`/users/${user.id}`}
                                        className="btn btn-outline-primary"
                                        title="View Profile"
                                      >
                                        <i className="fas fa-eye"></i>
                                      </Link>
                                      {user.role !== 'admin' && (
                                        <button
                                          className="btn btn-outline-warning"
                                          onClick={() => handlePromoteUser(user)}
                                          title="Promote to Admin"
                                        >
                                          <i className="fas fa-crown"></i>
                                        </button>
                                      )}
                                      {user.account_status === 'locked' ? (
                                        <button
                                          className="btn btn-outline-success"
                                          onClick={() => handleUnlockUser(user)}
                                          title="Unlock Account"
                                        >
                                          <i className="fas fa-unlock"></i>
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-outline-danger"
                                          onClick={() => handleLockUser(user)}
                                          title="Lock Account"
                                          disabled={user.role === 'admin'}
                                        >
                                          <i className="fas fa-lock"></i>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                          <nav aria-label="Search results pagination" className="mt-4">
                            <ul className="pagination justify-content-center">
                              <li className={`page-item ${!pagination.hasPrev ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pagination.page - 1)}
                                  disabled={!pagination.hasPrev}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                  Previous
                                </button>
                              </li>

                              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                const pageNum = Math.max(1, pagination.page - 2) + i;
                                if (pageNum <= pagination.totalPages) {
                                  return (
                                    <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                                      <button
                                        className="page-link"
                                        onClick={() => handlePageChange(pageNum)}
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                }
                                return null;
                              })}

                              <li className={`page-item ${!pagination.hasNext ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pagination.page + 1)}
                                  disabled={!pagination.hasNext}
                                >
                                  Next
                                  <i className="fas fa-chevron-right ms-1"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Locked Users Tab */}
      {activeTab === 'locked' && (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-lock me-2"></i>
                  Locked User Accounts
                  <span className="badge bg-danger ms-2">{lockedUsers?.length || 0}</span>
                </h5>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={loadLockedUsers}
                  disabled={isLoadingLocked}
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </button>
              </div>
              <div className="card-body">
                {isLoadingLocked ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading locked users...</p>
                  </div>
                ) : !lockedUsers || lockedUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-unlock fa-4x text-success mb-4"></i>
                    <h4 className="text-muted">No Locked Accounts</h4>
                    <p className="text-muted">
                      All user accounts are currently active and accessible.
                    </p>
                    <div className="alert alert-info mt-4">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Support Contact:</strong> {supportEmail}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Locked Account Information:</strong> Users with locked accounts cannot log in and will see a message directing them to contact <strong>{supportEmail}</strong> for assistance.
                    </div>

                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th style={{ width: '68px' }}></th>
                            <th>User</th>
                            <th>Locked Date</th>
                            <th>Locked By</th>
                            <th>Reason</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lockedUsers && lockedUsers.map((user) => (
                            <tr key={user.user_id}>
                              <td style={{ verticalAlign: 'middle' }}>
                                <ProfileAvatar
                                  imageUrl={user.profile_image_url}
                                  name={user.name || 'Unknown User'}
                                  size="md"
                                />
                              </td>
                              <td style={{ verticalAlign: 'middle' }}>
                                <div>
                                  <strong>{user.name || 'No name provided'}</strong>
                                  <br />
                                  <small className="text-muted">{user.email}</small>
                                  <br />
                                  <small className="text-muted">@{user.username}</small>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <strong>{new Date(user.locked_at).toLocaleDateString()}</strong>
                                  <br />
                                  <small className="text-muted">
                                    {new Date(user.locked_at).toLocaleTimeString()}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">{user.locked_by}</small>
                              </td>
                              <td>
                                {user.lock_reason ? (
                                  <span className="text-muted">
                                    {user.lock_reason.length > 50
                                      ? `${user.lock_reason.substring(0, 50)}...`
                                      : user.lock_reason
                                    }
                                  </span>
                                ) : (
                                  <span className="text-muted fst-italic">No reason provided</span>
                                )}
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <Link
                                    to={`/users/${user.user_id}`}
                                    className="btn btn-outline-primary"
                                    title="View Profile"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Link>
                                  <button
                                    className="btn btn-outline-success"
                                    onClick={() => handleUnlockUser(user)}
                                    title="Unlock Account"
                                  >
                                    <i className="fas fa-unlock"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Actions Audit Log */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Recent Administrative Actions
              </h6>
            </div>
            <div className="card-body">
              <AdminAuditLog maxEntries={15} showTitle={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Lock User Modal */}
      {showLockModal && selectedUser && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-lock text-danger me-2"></i>
                  Lock User Account
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLockModal(false)}
                  disabled={isProcessing}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Warning:</strong> This will prevent the user from logging in to their account.
                </div>

                <div className="mb-3">
                  <h6>User Information:</h6>
                  <div className="d-flex align-items-start gap-3">
                    <ProfileAvatar
                      imageUrl={'profile_image_url' in selectedUser ? selectedUser.profile_image_url : undefined}
                      name={selectedUser.name || 'Unknown User'}
                      size="lg"
                    />
                    <ul className="list-unstyled mb-0">
                      <li><strong>Name:</strong> {selectedUser.name || 'No name provided'}</li>
                      <li><strong>Email:</strong> {selectedUser.email}</li>
                      <li><strong>Username:</strong> {selectedUser.username}</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="lockReason" className="form-label">
                    Reason for Locking (Optional)
                  </label>
                  <textarea
                    className="form-control"
                    id="lockReason"
                    rows={3}
                    value={lockReason}
                    onChange={(e) => setLockReason(e.target.value)}
                    placeholder="Provide a reason for locking this account..."
                    maxLength={500}
                    disabled={isProcessing}
                  />
                  <div className="form-text">
                    {lockReason.length}/500 characters
                  </div>
                </div>

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>What happens when an account is locked:</strong>
                  <ul className="mb-0 mt-2">
                    <li>User cannot log in to their account</li>
                    <li>User will see a message directing them to contact {supportEmail}</li>
                    <li>This action will be logged for audit purposes</li>
                    <li>Account can be unlocked at any time by an admin</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLockModal(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmLockUser}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Locking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-lock me-2"></i>
                      Lock Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock User Modal */}
      {showUnlockModal && selectedUser && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-unlock text-success me-2"></i>
                  Unlock User Account
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUnlockModal(false)}
                  disabled={isProcessing}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  <strong>Confirmation:</strong> This will restore the user's access to their account.
                </div>

                <div className="mb-3">
                  <h6>User Information:</h6>
                  <div className="d-flex align-items-start gap-3">
                    <ProfileAvatar
                      imageUrl={'profile_image_url' in selectedUser ? selectedUser.profile_image_url : undefined}
                      name={selectedUser.name || 'Unknown User'}
                      size="lg"
                    />
                    <ul className="list-unstyled mb-0">
                      <li><strong>Name:</strong> {selectedUser.name || 'No name provided'}</li>
                      <li><strong>Email:</strong> {selectedUser.email}</li>
                      <li><strong>Username:</strong> {selectedUser.username}</li>
                    </ul>
                  </div>
                </div>

                {'locked_at' in selectedUser && selectedUser.locked_at && (
                  <div className="mb-3">
                    <h6>Lock Information:</h6>
                    <ul className="list-unstyled">
                      <li><strong>Locked:</strong> {new Date(selectedUser.locked_at).toLocaleString()}</li>
                      {'locked_by' in selectedUser && selectedUser.locked_by && (
                        <li><strong>Locked By:</strong> {selectedUser.locked_by}</li>
                      )}
                      {'lock_reason' in selectedUser && selectedUser.lock_reason && (
                        <li><strong>Reason:</strong> {selectedUser.lock_reason}</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>What happens when an account is unlocked:</strong>
                  <ul className="mb-0 mt-2">
                    <li>User can log in to their account normally</li>
                    <li>All account restrictions are removed</li>
                    <li>This action will be logged for audit purposes</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUnlockModal(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmUnlockUser}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Unlocking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-unlock me-2"></i>
                      Unlock Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promote to Admin Modal */}
      {showPromoteModal && selectedUserForPromotion && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-crown me-2 text-warning"></i>
                  Promote to Administrator
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPromoteModal(false)}
                  disabled={isPromoting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Important:</strong> This action will grant full administrative privileges to this user.
                </div>

                <div className="mb-3">
                  <h6>User Details:</h6>
                  <div className="d-flex align-items-start gap-3">
                    <ProfileAvatar
                      imageUrl={selectedUserForPromotion.profile_image_url}
                      name={selectedUserForPromotion.name || 'Unknown User'}
                      size="lg"
                    />
                    <ul className="list-unstyled mb-0">
                      <li><strong>Name:</strong> {selectedUserForPromotion.name || 'No name provided'}</li>
                      <li><strong>Email:</strong> {selectedUserForPromotion.email}</li>
                      <li><strong>Username:</strong> {selectedUserForPromotion.username}</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="promotionMessage" className="form-label">
                    Optional Message (will be included in notification email):
                  </label>
                  <textarea
                    id="promotionMessage"
                    className="form-control"
                    rows={3}
                    value={promotionMessage}
                    onChange={(e) => setPromotionMessage(e.target.value)}
                    placeholder="Add a personal message to the notification email (optional)..."
                    maxLength={500}
                    disabled={isPromoting}
                  />
                  <small className="text-muted">
                    {promotionMessage.length}/500 characters
                  </small>
                </div>

                <div className="alert alert-info mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  The user will receive an email notification about their new admin status.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPromoteModal(false)}
                  disabled={isPromoting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={confirmPromoteUser}
                  disabled={isPromoting}
                >
                  {isPromoting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Promoting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-crown me-2"></i>
                      Confirm Promotion
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
